import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormik } from 'formik';
import React from 'react';
import { RxCross2 } from 'react-icons/rx';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { ParentApi } from '../API/ParentApi';
import Button from '../Components/Button';
import { SUPPORTED_GRADES } from '../Constants/grades';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import { User } from '../Types/User';

interface CreateStudentViewProps {
  onCreated: (studentUser: User) => void;
  onCancel?: () => void;
}

const validationSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  grade: yup.number().required('Grade is required'),
  email: yup.string().email('Invalid email format'),
});

const CreateStudentView: React.FC<CreateStudentViewProps> = ({
  onCancel,
  onCreated,
}) => {
  const { trackEvent } = useAnalytics();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      grade: '',
      email: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_CREATE_REQUESTED, {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        grade: Number(values.grade),
        email: values.email.trim() || null,
      });

      try {
        const parentApi = new ParentApi();

        const user = await parentApi.createStudent({
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          grade: Number(values.grade),
          email: values.email.trim() || undefined,
        });

        trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_CREATE_COMPLETED, {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          grade: Number(values.grade),
          lexile: user.lexileId,
          email: values.email.trim() || null,
        });

        onCreated?.(user);
      } catch (error) {
        console.error('Failed to create student', error);
        toast.error('Failed to create student. Please try again.');

        trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_CREATE_FAILED, {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          grade: Number(values.grade),
          email: values.email.trim() || null,
          error: error.response?.data ?? error.message,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <form
      className="flex flex-col bg-white rounded-lg p-8 gap-4"
      onSubmit={formik.handleSubmit}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          onCancel?.();
        }
      }}
    >
      <header className="flex flex-col items-center justify-between gap-2">
        <h1 className="text-2xl font-bold">Create an Account</h1>
        <p className="text-sm text-gray-500">
          Create an account for your child that will allow them to explore
          personalized content tailored to their interests and reading level.
        </p>

        <Button
          variant="round"
          onClick={onCancel}
          className="absolute top-4 right-4"
        >
          <RxCross2 className="w-4 h-4 text-white" />
        </Button>
      </header>

      <main className="flex flex-col py-2 gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="firstName"
            className="font-bold text-sm text-neutral-800 text-left"
          >
            First Name *
          </label>
          <input
            data-testid="create-student-first-name"
            id="firstName"
            name="firstName"
            type="text"
            placeholder="Enter first name..."
            aria-label="First Name"
            className="text-gray-500 text-base leading-6 whitespace-nowrap items-stretch border border-[color:var(--gray-400,#CED4DA)] bg-white justify-center pl-3.5 pr-1.5 py-3 rounded-lg border-solid"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.firstName}
          />
          {formik.touched.firstName && formik.errors.firstName && (
            <div className="text-red-600 text-sm">
              {formik.errors.firstName}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="lastName"
            className="font-bold text-sm text-neutral-800 text-left"
          >
            Last Name *
          </label>
          <input
            id="lastName"
            data-testid="create-student-last-name"
            name="lastName"
            type="text"
            placeholder="Enter last name..."
            aria-label="Last Name"
            className="text-gray-500 text-base leading-6 whitespace-nowrap items-stretch border border-[color:var(--gray-400,#CED4DA)] bg-white justify-center pl-3.5 pr-1.5 py-3 rounded-lg border-solid"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.lastName}
          />
          {formik.touched.lastName && formik.errors.lastName && (
            <div className="text-red-600 text-sm">{formik.errors.lastName}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="grade"
            className="font-bold text-sm text-neutral-800 text-left"
          >
            Grade *
          </label>
          <select
            id="grade"
            data-testid="create-student-grade"
            name="grade"
            aria-label="Grade"
            className="text-gray-500 text-base leading-6 whitespace-nowrap items-stretch border border-[color:var(--gray-400,#CED4DA)] bg-white justify-center pl-3.5 pr-1.5 py-3 rounded-lg border-solid"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.grade}
          >
            <option value="">Select a grade</option>
            {SUPPORTED_GRADES.map((grade) => (
              <option key={grade.value} value={grade.value.toString()}>
                {grade.title}
              </option>
            ))}
          </select>
          {formik.touched.grade && formik.errors.grade && (
            <div className="text-red-600 text-sm">{formik.errors.grade}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label
            htmlFor="email"
            className="font-bold text-sm text-neutral-800 text-left"
          >
            Email
          </label>
          <input
            id="email"
            data-testid="create-student-email"
            name="email"
            type="email"
            placeholder="Enter email address..."
            aria-label="Email"
            className="text-gray-500 text-base leading-6 whitespace-nowrap items-stretch border border-[color:var(--gray-400,#CED4DA)] bg-white justify-center pl-3.5 pr-1.5 py-3 rounded-lg border-solid"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.email}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="text-red-600 text-sm">{formik.errors.email}</div>
          )}
        </div>
      </main>
      <footer className="flex justify-end bg-white rounded-lg">
        <div className="flex gap-2">
          <Button
            type="submit"
            testId="create-student-button"
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            {formik.isSubmitting && (
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin h-5 w-5 mr-1 align-middle"
              />
            )}
            Create Account
          </Button>
        </div>
      </footer>
    </form>
  );
};

export default CreateStudentView;
